import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import BottomHeader from './Bottom';
import { useLocation, useNavigate } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import useQueryUrl from 'src/hooks/useQuery';
import ModalManageObjectRetail from '../../ModalManageObjectRetail';
import GroupObjectRetail from '../../GroupObjectRetail';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ObjectPreviewRetail from '../../ObjectPreviewRetail';
import TableViewIcon from '@mui/icons-material/TableView';
import ModalManageObjectByGroup from 'src/components/ModalManageObjectByGroup';
import EcommerceObjectPreview from 'src/components/EcommerceObjectPreview';
import useEcommerce from 'src/services/useEcommerce';
import {
  reducerUpdateIsSelectedAllPrimary,
  reducerUpdateStreamEcommerceSelected
} from 'src/features/ecommerceStream';
import { reducerUpdateObjectListByObjectType } from 'src/features/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { concatUniqueObjectsById } from 'src/utils/concatUniqueObjectsById';
import { useAlert } from 'src/hooks/useAlert';
import BottomHeaderEcommerce from 'src/layouts/SidebarLayout/Header/BottomEcommerce';

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    z-index: 5;
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: white;
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

const HeaderWrapperSec = styled(Box)(
  ({ theme }) => `
    background: #F5F5F5;
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    z-index: 5;
    justify-content: space-between;
    width: 100%;
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

function HeaderRetail() {
  const { pathname } = useLocation();
  const menu = pathname.split('/')[1];
  const title = pathname.split('/')[2];
  const query = useQueryUrl();
  const tabQuery = query.get('tab');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<string>(tabQuery);

  const { handleClickAlert } = useAlert();
  const projectId = useAppSelector((state) => state.users.project.id);
  const { objectIdList } = useAppSelector((state) => state.analyticArguments);
  const {
    groupSelectedById: selectedGroupId,
    objectListByObjectType,
    selectedGroups,
    ecommerceObjectList
  } = useAppSelector((state) => state.objectManagement);
  const { useGetEcommerceObjectList, useGetEcommerceGroups } = useEcommerce();

  const [getObjectList] = useGetEcommerceObjectList();
  const { refetch: getGroupObject } = useGetEcommerceGroups({
    skip: true
  });

  const handleRefresh = () => {
    dispatch(reducerUpdateStreamEcommerceSelected([]));
    dispatch(reducerUpdateIsSelectedAllPrimary(false));
    Promise.all([
      getObjectList({
        variables: {
          projectId
        }
      }),
      getGroupObject({ projectId: projectId, templateName: 'content' })
    ])
      .then((responses) => {
        // if a group is selected
        if (selectedGroupId !== '_default' || selectedGroups.length > 0) {
          const newGroupObjectList = responses[1].data;
          const prevSelectedGroups = selectedGroups;

          const prevSelectedGroupIds = prevSelectedGroups
            .map((group) => group.objectList.map((group) => group.id))
            .flat();

          const newSelectedGroups =
            newGroupObjectList?.objectManagement_getObjectGroupList?.filter(
              (group) =>
                prevSelectedGroups.some(
                  (prevGroup) => prevGroup.id === group.id
                )
            );

          const allObjectsFromGroups = newSelectedGroups.flatMap(
            (group) => group.objectList
          );

          // semua object yang ada di active object + object baru di grup
          const finalActiveObjects = allObjectsFromGroups.filter(
            (object) =>
              objectIdList.includes(object.id) ||
              !prevSelectedGroupIds.includes(object.id)
          );
          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
          // if no group selected
        } else {
          const newObjectList = responses[0].data.ecommerce_getObject;
          // get only new objects
          const newObjects = newObjectList.filter((object) => {
            const prevObjectListIds = ecommerceObjectList.map(
              (object) => object.id
            );
            return !prevObjectListIds.includes(object.id);
          });

          const selectedObjects = newObjectList.filter((object) =>
            objectIdList.includes(object.id)
          );
          const finalActiveObjects = concatUniqueObjectsById(
            selectedObjects,
            newObjects
          );

          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
        }
      })
      .catch((error) =>
        handleClickAlert({
          vertical: 'top',
          horizontal: 'center',
          message: "Couldn't fetch data, please try again.",
          severity: 'error'
        })
      );
  };

  const objectListPrevies = useMemo(() => {
    if (title === 'content' || title === 'community') {
      return objectListByObjectType.filter(
        (object) => object.streamType === 'account'
      );
    }
    return objectListByObjectType;
  }, [objectListByObjectType, title]);

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    navigate(`${pathname}?tab=${newValue}`);
  };

  useEffect(() => {
    setTab(tabQuery);
  }, [tabQuery]);

  const isNetworkAnalysis = () => {
    return title === 'sna' || title === 'dna';
  };

  const isClustering = () => {
    return title === 'clustering';
  };

  const isGoogleReview = menu === 'google_review';
  const isEcommerce = menu === 'ecommerce';

  const theme = useTheme();
  const isLgScreen = useMediaQuery(theme.breakpoints.up('sm'));

  const TabSocialMedia = () => {
    return (
      <Stack
        sx={{
          borderRadius: '16px',
          backgroundColor: theme.colors.primary.light
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{
              backgroundColor:
                tab === 'widget' || !tab
                  ? theme.colors.primary.main
                  : theme.colors.primary.light,
              py: 1,
              px: 2,
              borderRadius: '16px',
              cursor: 'pointer'
            }}
            onClick={() => handleChange(null, 'widget')}
          >
            <WidgetsIcon
              sx={{
                color: tab === 'widget' || !tab ? 'white' : '#757575',
                fontSize: '16px'
              }}
            />
            <Typography
              fontWeight={600}
              fontSize="14px"
              color={tab === 'widget' || !tab ? 'white' : '#757575'}
            >
              Widget
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{
              backgroundColor:
                tab === 'stream_preview'
                  ? theme.colors.primary.main
                  : theme.colors.primary.light,
              py: 1,
              px: 2,
              borderRadius: '16px',
              cursor: 'pointer',
              flex: 1
            }}
            onClick={() => handleChange(null, 'stream_preview')}
          >
            <TableViewIcon
              sx={{
                color: tab === 'stream_preview' ? 'white' : '#757575',
                fontSize: '16px'
              }}
            />
            <Typography
              fontWeight={600}
              fontSize="14px"
              color={tab === 'stream_preview' ? 'white' : '#757575'}
            >
              Stream Preview
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const TabSnaDna = () => {
    return (
      <>
        <Stack
          sx={{
            borderRadius: '16px',
            backgroundColor: theme.colors.primary.light
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                backgroundColor:
                  tab !== 'metrics'
                    ? theme.colors.primary.main
                    : theme.colors.primary.light,
                py: 1,
                px: 2,
                borderRadius: '16px',
                cursor: 'pointer'
              }}
              onClick={() => handleChange(null, title)}
            >
              <WidgetsIcon
                sx={{
                  color: tab !== 'metrics' ? 'white' : '#757575',
                  fontSize: '16px'
                }}
              />
              <Typography
                fontWeight={600}
                fontSize="14px"
                color={tab !== 'metrics' ? 'white' : '#757575'}
              >
                {title.toUpperCase()}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                backgroundColor:
                  tab === 'metrics'
                    ? theme.colors.primary.main
                    : theme.colors.primary.light,
                py: 1,
                px: 2,
                borderRadius: '16px',
                cursor: 'pointer'
              }}
              onClick={() => handleChange(null, 'metrics')}
            >
              <TableViewIcon
                sx={{
                  color: tab === 'metrics' ? 'white' : '#757575',
                  fontSize: '16px'
                }}
              />
              <Typography
                fontWeight={600}
                fontSize="14px"
                color={tab === 'metrics' ? 'white' : '#757575'}
              >
                Metrics
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </>
    );
  };

  return (
    <>
      <HeaderWrapper display="flex" alignItems="center">
        <Grid container>
          <Grid item sm={12} width="inherit">
            <GroupObjectRetail />
          </Grid>
          <Grid item sm={12}>
            <Stack
              direction={isLgScreen ? 'row' : 'column'}
              alignItems={isLgScreen ? 'center' : 'left'}
              justifyContent={isLgScreen ? 'space-between' : 'normal'}
              pb={1}
            >
              <Stack
                direction={isLgScreen ? 'row' : 'column'}
                alignItems={isLgScreen ? 'center' : 'start'}
                spacing={1}
                mt={1.5}
                mb={2.1}
              >
                {isEcommerce || isGoogleReview ? (
                  <ModalManageObjectByGroup />
                ) : (
                  <ModalManageObjectRetail />
                )}
                {isEcommerce ? (
                  <EcommerceObjectPreview objectList={objectListPrevies} />
                ) : (
                  <ObjectPreviewRetail objectList={objectListPrevies} />
                )}
              </Stack>
              {!isClustering() && !isEcommerce && !isGoogleReview ? (
                isNetworkAnalysis() ? (
                  <TabSnaDna />
                ) : (
                  <TabSocialMedia />
                )
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </HeaderWrapper>
      <HeaderWrapperSec display="flex" alignItems="center">
        <Grid container>
          <Grid item sm={12}>
            {isEcommerce ? (
              <BottomHeaderEcommerce key={title} onRefresh={handleRefresh} />
            ) : (
              <BottomHeader key={title} />
            )}
          </Grid>
        </Grid>
      </HeaderWrapperSec>
    </>
  );
}

export default HeaderRetail;
